import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Bot, Link2, Unlink, Copy, Check, Loader2, HelpCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";

interface TelegramSettingsProps {
  /** Omit or pass null for the personal workspace */
  groupId?: string | null;
  isAdmin: boolean;
}

export function TelegramSettings({ groupId, isAdmin }: TelegramSettingsProps) {
  const scope = groupId ? { groupId } : {};
  const [botConnected, setBotConnected] = useState(false);
  const [botUsername, setBotUsername] = useState<string | null>(null);
  const [userLinked, setUserLinked] = useState(false);
  const [botToken, setBotToken] = useState("");
  const [linkCode, setLinkCode] = useState<string | null>(null);
  const [linkInstructions, setLinkInstructions] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, [groupId]);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke('telegram-setup', {
        body: { action: 'get_status', ...scope },
      });
      if (error) throw error;
      setBotConnected(data.botConnected);
      setBotUsername(data.botUsername);
      setUserLinked(data.userLinked);
    } catch (err: any) {
      console.error('Error fetching telegram status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectBot = async () => {
    if (!botToken.trim()) {
      toast.error("Please enter a bot token");
      return;
    }
    try {
      setConnecting(true);
      const { data, error } = await supabase.functions.invoke('telegram-setup', {
        body: { action: 'connect_bot', ...scope, botToken: botToken.trim() },
      });
      if (error) throw error;
      if (data.error) {
        toast.error(data.error);
        return;
      }
      toast.success(data.message);
      setBotToken("");
      fetchStatus();
    } catch (err: any) {
      toast.error("Failed to connect bot", { description: err.message });
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnectBot = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('telegram-setup', {
        body: { action: 'disconnect_bot', ...scope },
      });
      if (error) throw error;
      toast.success(data.message);
      fetchStatus();
    } catch (err: any) {
      toast.error("Failed to disconnect bot", { description: err.message });
    }
  };

  const handleGenerateLinkCode = async () => {
    try {
      setGenerating(true);
      const { data, error } = await supabase.functions.invoke('telegram-setup', {
        body: { action: 'generate_link_code', ...scope },
      });
      if (error) throw error;
      if (data.error) {
        toast.error(data.error);
        return;
      }
      setLinkCode(data.linkCode);
      setLinkInstructions(data.instructions);
    } catch (err: any) {
      toast.error("Failed to generate link code", { description: err.message });
    } finally {
      setGenerating(false);
    }
  };

  const handleUnlink = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('telegram-setup', {
        body: { action: 'unlink', ...scope },
      });
      if (error) throw error;
      toast.success(data.message);
      setUserLinked(false);
      setLinkCode(null);
      setLinkInstructions(null);
    } catch (err: any) {
      toast.error("Failed to unlink", { description: err.message });
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bot className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">Telegram Bot</span>
          {botConnected && (
            <Badge variant="secondary" className="text-xs">
              @{botUsername}
            </Badge>
          )}
        </div>
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="ghost" size="icon" className="h-7 w-7">
              <HelpCircle className="h-3.5 w-3.5 text-muted-foreground" />
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Telegram Integration Guide</DialogTitle>
              <DialogDescription>Follow these steps to connect and use the Telegram bot.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 text-sm">
              <div className="space-y-1">
                <p className="font-semibold text-foreground">Step 1: Create a Bot</p>
                <p className="text-muted-foreground">
                  Open Telegram and message{" "}
                  <a href="https://t.me/BotFather" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">@BotFather</a>.
                  Send <code className="bg-muted px-1 rounded text-xs">/newbot</code>, follow the prompts, and copy the bot token it gives you.
                </p>
              </div>
              <div className="space-y-1">
                <p className="font-semibold text-foreground">Step 2: Connect the Bot (Admin)</p>
                <p className="text-muted-foreground">
                  Paste the bot token into the input field below and click <strong>Connect</strong>. Only group admins can do this step.
                </p>
              </div>
              <div className="space-y-1">
                <p className="font-semibold text-foreground">Step 3: Link Your Account</p>
                <p className="text-muted-foreground">
                  Click <strong>Link your Telegram</strong> to get a unique code. Open a chat with the bot in Telegram and send the code as shown (e.g. <code className="bg-muted px-1 rounded text-xs">/start abc123</code>).
                </p>
              </div>
              <div className="space-y-1">
                <p className="font-semibold text-foreground">Step 4: Start Using It</p>
                <p className="text-muted-foreground">
                  Once linked, message the bot in natural language just like the in-app AI assistant. Examples:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-0.5 ml-1">
                  <li>"Add a new lead named John from Acme Corp"</li>
                  <li>"Schedule a follow-up call for John on Monday at 10am"</li>
                  <li>"Show me all hot leads"</li>
                  <li>"Update John's status to closed"</li>
                  <li>"Delete the meeting with Acme Corp"</li>
                </ul>
              </div>
              <div className="space-y-1">
                <p className="font-semibold text-foreground">Voice Messages</p>
                <p className="text-muted-foreground">
                  You can also send voice messages, the bot will transcribe and process them automatically.
                </p>
              </div>
              <div className="space-y-1">
                <p className="font-semibold text-foreground">Bot Commands</p>
                <ul className="list-disc list-inside text-muted-foreground space-y-0.5 ml-1">
                  <li><code className="bg-muted px-1 rounded text-xs">/help</code>, Show available commands</li>
                  <li><code className="bg-muted px-1 rounded text-xs">/status</code>, Check connection status</li>
                  <li><code className="bg-muted px-1 rounded text-xs">/unlink</code>, Unlink your Telegram account</li>
                </ul>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Bot Connection (Admin Only) */}
      {isAdmin && !botConnected && (
        <div className="space-y-2 bg-muted/50 rounded-md p-3">
          <p className="text-xs text-muted-foreground">
            {groupId
              ? "Connect a Telegram bot to let group members manage leads via Telegram."
              : "Connect a Telegram bot to manage your personal leads via Telegram."}
            Create one at{" "}
            <a href="https://t.me/BotFather" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
              @BotFather
            </a>
          </p>
          <div className="flex gap-2">
            <Input
              value={botToken}
              onChange={(e) => setBotToken(e.target.value)}
              placeholder="Paste your bot token..."
              type="password"
              className="h-8 text-sm"
            />
            <Button onClick={handleConnectBot} size="sm" className="h-8 shrink-0" disabled={connecting}>
              {connecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Connect"}
            </Button>
          </div>
        </div>
      )}

      {isAdmin && botConnected && (
        <div className="flex items-center justify-between bg-muted/50 rounded-md p-3">
          <div className="text-xs text-muted-foreground">
            Bot <span className="font-medium text-foreground">@{botUsername}</span> is active
          </div>
          <Button variant="ghost" size="sm" className="h-7 text-xs text-destructive" onClick={handleDisconnectBot}>
            <Unlink className="h-3 w-3 mr-1" />
            Disconnect
          </Button>
        </div>
      )}

      {/* User Linking */}
      {botConnected && (
        <div className="space-y-2">
          {userLinked ? (
            <div className="flex items-center justify-between bg-primary/10 border border-primary/20 rounded-md p-3">
              <div className="flex items-center gap-2">
                <Link2 className="h-3.5 w-3.5 text-primary" />
                <span className="text-xs font-medium text-primary">
                  Your Telegram is linked
                </span>
              </div>
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={handleUnlink}>
                Unlink
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {linkCode ? (
                <div className="bg-muted/50 rounded-md p-3 space-y-2">
                  <p className="text-xs text-muted-foreground">{linkInstructions}</p>
                  <div className="flex items-center gap-2">
                    <code className="bg-background px-2 py-1 rounded text-sm font-mono flex-1">
                      /start {linkCode}
                    </code>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-7 w-7 shrink-0"
                      onClick={() => handleCopy(`/start ${linkCode}`)}
                    >
                      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full h-8 text-xs"
                  onClick={handleGenerateLinkCode}
                  disabled={generating}
                >
                  {generating ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                  ) : (
                    <Link2 className="h-3.5 w-3.5 mr-1" />
                  )}
                  Link your Telegram
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
